import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { Button, Icon } from '@uturn/ui-kit';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MinimalHeader } from './header';

interface ErrorFallbackBaseProps {
	icon: IconDefinition;
	title: string;
	message: string;
	troubleshootingHeader?: string;
	troubleshootingTips?: string[];
	actions?: ReactNode;
	onReset?: () => void;
	onGoHome?: () => void;
	onLogout?: () => void | Promise<void>;
	user?: { name: string; role: string } | null;
}

type ErrorReferenceProps =
	| {
			xid: string;
			errorReferenceLabel: string;
			errorReferenceDescription: string;
	  }
	| {
			xid?: never;
			errorReferenceLabel?: never;
			errorReferenceDescription?: never;
	  };

type ErrorFallbackProps = ErrorFallbackBaseProps & ErrorReferenceProps;

export const ErrorFallback = ({
	icon,
	title,
	message,
	xid,
	troubleshootingHeader,
	troubleshootingTips,
	errorReferenceLabel,
	errorReferenceDescription,
	actions,
	onReset,
	onGoHome,
	onLogout,
	user,
}: ErrorFallbackProps) => {
	const { t } = useTranslation();

	const handleReset = () => {
		// Call optional side effect callback
		onReset?.();
		// Default behavior: reload page
		window.location.reload();
	};

	const handleGoHome = () => {
		// Call optional side effect callback
		onGoHome?.();
		// Default behavior: redirect to home
		window.location.href = '/';
	};

	return (
		<div className="flex min-h-screen flex-col bg-background">
			{user && <MinimalHeader user={user} onLogout={onLogout} />}
			<div className="flex flex-1 items-center justify-center p-4">
				<div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-8 shadow-lg">
					<div className="flex items-center justify-center">
						<div className="rounded-full bg-destructive/10 p-3">
							<Icon className="h-8 w-8 text-destructive" icon={icon} />
						</div>
					</div>

					<div className="space-y-2 text-center">
						<h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
						<p className="text-sm text-muted-foreground">{message}</p>
					</div>

					<div className="space-y-3">
						{actions ?? (
							<div className="flex gap-2">
								<Button
									onClick={handleReset}
									className="flex-none"
									size="lg"
									variant="ghost"
								>
									<Icon className="mr-2 h-4 w-4" icon={faArrowsRotate} />
									{t('errors.reload-page', 'Reload page')}
								</Button>
								<Button onClick={handleGoHome} className="flex-1" size="lg">
									{t('errors.go-home', 'Go to home')}
								</Button>
							</div>
						)}

						{troubleshootingHeader &&
							troubleshootingTips &&
							troubleshootingTips.length > 0 && (
								<div className="rounded-md bg-muted/50 p-4 text-sm text-muted-foreground">
									<p className="mb-2 font-medium">{troubleshootingHeader}</p>
									<ul className="ml-4 space-y-1 list-disc">
										{troubleshootingTips.map((tip) => (
											<li key={tip}>{tip}</li>
										))}
									</ul>
								</div>
							)}

						{xid !== undefined && (
							<div className="rounded-md bg-muted/50 p-4 text-center">
								<p className="text-xs font-medium text-muted-foreground">
									{errorReferenceLabel}
								</p>
								<p className="mt-1 font-mono text-lg font-semibold tracking-wider">
									{xid}
								</p>
								<p className="mt-2 text-xs text-muted-foreground">
									{errorReferenceDescription}
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
